import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, TextField, Typography,
} from '@mui/material';
import { ProcurementSettingsService } from '../../services/procurementSettings.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { ProcurementSettingsUpdateDTO } from '../../interfaces/procurement/procurementSettings.interface';
import { Account } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';

const EMPTY_DTO: ProcurementSettingsUpdateDTO = {
  inventoryAccountId: '',
  inventoryAdjustmentAccountId: '',
  grniAccountId: '',
  accountsPayableAccountId: '',
  inputTaxAccountId: '',
  poApprovalThreshold: 0,
  priceVarianceAccountId: '',
};

// The backend does not constrain the price-variance account's type; it posts DR when the supplier
// charged more than the PO basis and CR when less, so the natural homes are EXPENSE (a purchase
// price variance account) or REVENUE. An already-saved account of another type stays selectable.
const PRICE_VARIANCE_TYPES: Account['accountType'][] = ['EXPENSE', 'REVENUE'];

// Kept as text so an untouched field round-trips exactly what the server returned.
const isNonNegative = (v: string) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= 0;

export const ProcurementSettings: React.FC = () => {
  const toast = useToast();
  const [form, setForm] = useState<ProcurementSettingsUpdateDTO>(EMPTY_DTO);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // A failed load must not leave an editable empty form: saving it would null every mapped account.
  const [loadFailed, setLoadFailed] = useState(false);
  const [priceTolPct, setPriceTolPct] = useState('');
  const [taxTol, setTaxTol] = useState('');

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setLoading(true);
      setLoadFailed(false);
      // The backend creates a default row on first read, so a failure here is a real error, not "no settings yet".
      const [settings, accountsRes] = await Promise.all([
        ProcurementSettingsService.getByInstitution(institutionId),
        AccountService.getAllAccounts(),
      ]);
      setAccounts(accountsRes ?? []);
      if (settings) {
        setForm({
          inventoryAccountId: settings.inventoryAccountId ?? '',
          inventoryAdjustmentAccountId: settings.inventoryAdjustmentAccountId ?? '',
          grniAccountId: settings.grniAccountId ?? '',
          accountsPayableAccountId: settings.accountsPayableAccountId ?? '',
          inputTaxAccountId: settings.inputTaxAccountId ?? '',
          poApprovalThreshold: settings.poApprovalThreshold ?? 0,
          priceVarianceAccountId: settings.priceVarianceAccountId ?? '',
        });
        setPriceTolPct(settings.priceVarianceTolerancePct != null ? String(settings.priceVarianceTolerancePct) : '');
        setTaxTol(settings.taxVarianceTolerance != null ? String(settings.taxVarianceTolerance) : '');
      }
    } catch (e) {
      setLoadFailed(true);
      toast.showApiError(e, 'Failed to load procurement settings');
    } finally {
      setLoading(false);
    }
  };

  const priceTolInvalid = priceTolPct.trim() !== '' && !isNonNegative(priceTolPct);
  const taxTolInvalid = taxTol.trim() !== '' && !isNonNegative(taxTol);

  const handleSave = async () => {
    if (loadFailed || priceTolInvalid || taxTolInvalid) return;
    const institutionId = AuthService.getSelectedInstitution()!!;
    try {
      setSaving(true);
      // A blank tolerance is omitted, which the backend treats as "keep the stored value".
      await ProcurementSettingsService.upsert(institutionId, {
        ...form,
        priceVarianceTolerancePct: priceTolPct.trim() === '' ? undefined : Number(priceTolPct),
        taxVarianceTolerance: taxTol.trim() === '' ? undefined : Number(taxTol),
      });
      toast.showSuccess('Procurement settings saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save procurement settings');
    } finally {
      setSaving(false);
    }
  };

  const priceVarianceAccounts = accounts.filter(a =>
    PRICE_VARIANCE_TYPES.includes(a.accountType) || a.id === form.priceVarianceAccountId);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  if (loadFailed) {
    return (
      <Box sx={{ p: 3, maxWidth: 720 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Procurement Settings</Typography>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadAll}>Retry</Button>}>
          Could not load the procurement settings. Saving is disabled so the stored accounts are not overwritten.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Procurement Settings</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <AccountAutocomplete
          accounts={accounts}
          value={form.inventoryAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inventoryAccountId: id })}
          label="Inventory Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={accounts}
          value={form.inventoryAdjustmentAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inventoryAdjustmentAccountId: id })}
          label="Inventory Adjustment Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={accounts}
          value={form.grniAccountId ?? ''}
          onChange={(id) => setForm({ ...form, grniAccountId: id })}
          label="GRNI Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={accounts}
          value={form.accountsPayableAccountId ?? ''}
          onChange={(id) => setForm({ ...form, accountsPayableAccountId: id })}
          label="Accounts Payable Account"
          placeholder="None"
        />

        <AccountAutocomplete
          accounts={accounts}
          value={form.inputTaxAccountId ?? ''}
          onChange={(id) => setForm({ ...form, inputTaxAccountId: id })}
          label="Input Tax Account"
          placeholder="None"
        />

        <TextField
          label="PO Approval Threshold"
          type="number"
          value={form.poApprovalThreshold}
          onChange={e => setForm({ ...form, poApprovalThreshold: Number(e.target.value) })}
          inputProps={{ 'aria-label': 'PO Approval Threshold' }}
          fullWidth
        />

        <AccountAutocomplete
          accounts={priceVarianceAccounts}
          value={form.priceVarianceAccountId ?? ''}
          onChange={(id) => setForm({ ...form, priceVarianceAccountId: id })}
          label="Price Variance Account"
          placeholder="None"
          helperText="Required to post a supplier bill whose price differs from the PO. Expense or revenue accounts."
        />

        <TextField
          label="Price Variance Tolerance (%)"
          type="number"
          value={priceTolPct}
          onChange={e => setPriceTolPct(e.target.value)}
          error={priceTolInvalid}
          helperText={priceTolInvalid
            ? 'Enter 0 or more'
            : 'A supplier bill line priced more than this percent away from the PO price is blocked until released.'}
          inputProps={{ 'aria-label': 'Price Variance Tolerance', min: 0 }}
          fullWidth
        />

        <TextField
          label="Tax Variance Tolerance"
          type="number"
          value={taxTol}
          onChange={e => setTaxTol(e.target.value)}
          error={taxTolInvalid}
          helperText={taxTolInvalid
            ? 'Enter 0 or more'
            : 'Largest difference, in currency, allowed between the billed tax and the computed tax.'}
          inputProps={{ 'aria-label': 'Tax Variance Tolerance', min: 0 }}
          fullWidth
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving || priceTolInvalid || taxTolInvalid}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};
